import { useNavigate } from 'react-router-dom';
import { CalendarClock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { use_public_service_packages } from '@/features/service-package/hooks';
import type { ServicePackage } from '@/features/service-package/types';

function count_label(p: ServicePackage): string {
  const unit = p.module_type === 'TRAINER' ? 'class' : 'walk';
  const plural = p.module_type === 'TRAINER' ? 'es' : 's';
  return `${p.sessions_count} ${unit}${p.sessions_count > 1 ? plural : ''}`;
}

/**
 * Walker / trainer packages (client change, 9 Oct 2026): the vendor publishes
 * "7 days · 3 classes · ₹X"; the customer buys one and picks only the weekdays,
 * time and start date. Replaces the old customer-built "Set up plan" / "Buy a
 * course" schedule builder.
 */
export function ServicePackagesSection({ vendor_id, vendor_name }: { vendor_id: string; vendor_name: string | null }) {
  const navigate = useNavigate();
  const packages_q = use_public_service_packages(vendor_id);
  const packages = packages_q.data ?? [];

  if (packages_q.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-5 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading packages…
        </CardContent>
      </Card>
    );
  }
  if (packages.length === 0) return null;

  return (
    <Card data-testid="service-packages">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary" /> Packages
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col p-0">
        <ul className="divide-y-2 divide-brand-outline-variant/10">
          {packages.map((p) => (
            <li key={p.id} className="flex items-center gap-3 p-4" data-testid={`service-package-${p.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold">{p.name}</span>
                  <StatusPill tone="neutral">{p.duration_days} days</StatusPill>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {count_label(p)} · {p.sessions_per_week} day{p.sessions_per_week > 1 ? 's' : ''} a week of your choice
                  {p.session_minutes ? ` · ${p.session_minutes} min each` : ''}
                </p>
                {p.description && <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.description}</p>}
              </div>
              <div className="shrink-0 text-right tabular-nums" data-testid={`package-price-${p.id}`}>
                {p.compare_at_price_inr && (
                  <div className="text-xs text-muted-foreground line-through">₹{p.compare_at_price_inr}</div>
                )}
                <div className="font-heading text-base font-bold">₹{p.price_inr}</div>
                {p.discount_pct != null && <div className="text-xs font-semibold text-success">{p.discount_pct}% off</div>}
              </div>
              <Button
                size="sm"
                variant="gradient"
                className="shrink-0"
                onClick={() =>
                  navigate(p.module_type === 'TRAINER' ? `/app/package/${p.id}` : `/app/plan/${p.id}`, {
                    state: { offer: p, vendor_name, vendor_id },
                  })
                }
                data-testid={`buy-package-${p.id}`}
              >
                Buy package
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
